import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ShieldAlert,
  ClipboardCheck,
  Pencil,
  Undo2,
} from "lucide-react";
import { canApproveProposal } from "@ckgo2/shared/types";
import { api, ApiError, type ApiResponse } from "@/api/client";
import i18n from "@/i18n";
import { useAuthStore } from "@/stores/authStore";
import type { AgentProposal, AgentProposalSummary } from "./types";

// Localized labels for the proposal tier badge (read/write/money).
const TIER_LABELS: Record<AgentProposalSummary["tier"], string> = {
  read: i18n.t("shared.agent.tierRead"),
  write: i18n.t("shared.agent.tierWrite"),
  money: i18n.t("shared.agent.tierMoney"),
};

// Role gate uses the SHARED `canApproveProposal` (same source as the server's approve route) so
// the UI hides buttons a role can't use and can never drift from server enforcement.

type LocalStatus = "proposed" | "executed" | "rejected" | "failed";

/**
 * One agent proposal with the approval gate (Block 3 B3.1, extended in Block 4.7). Write
 * proposals execute on a single confirm; money/send proposals need a second explicit confirm.
 * As a plan step it shows its number and, when `editable` (an outgoing email), an Edit affordance
 * to read + rewrite the text before it's sent. `forcedStatus` lets a parent PlanPanel reflect the
 * outcome of an "approve all" without the card re-fetching.
 */
export default function ProposalCard({
  proposal,
  stepNumber,
  editable,
  forcedStatus,
  forcedError,
}: {
  proposal: AgentProposalSummary;
  stepNumber?: number;
  editable?: boolean;
  forcedStatus?: LocalStatus;
  forcedError?: string | null;
}) {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role ?? "readonly");
  const [status, setStatus] = useState<LocalStatus>("proposed");
  const [busy, setBusy] = useState<"approve" | "reject" | "edit" | "revert" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reverted, setReverted] = useState(false);
  const [revertNote, setRevertNote] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [summaryOverride, setSummaryOverride] = useState<string | null>(null);

  const allowed = canApproveProposal(proposal.tier, role);
  const isMoney = proposal.tier === "money";
  const effectiveStatus = forcedStatus ?? status;
  const shownError = forcedStatus === "failed" ? (forcedError ?? error) : error;
  const shownSummary = summaryOverride ?? proposal.summary;

  const approve = async (confirm: boolean) => {
    setBusy("approve");
    setError(null);
    try {
      const q = confirm ? "?confirm=1" : "";
      const res = await api.post<ApiResponse<AgentProposal>>(
        `/api/admin/agent/proposals/${proposal.id}/approve${q}`,
      );
      setStatus(res.data.status === "executed" ? "executed" : "failed");
      if (res.data.status === "failed")
        setError(res.data.error || t("shared.agent.executionFailed"));
    } catch (err) {
      // Money tier asks for a second confirmation — surface the inline confirm instead of erroring.
      if (err instanceof ApiError && err.code === "confirm_required") {
        setConfirming(true);
      } else {
        setError(err instanceof Error ? err.message : t("shared.agent.approveFailed"));
      }
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    setBusy("reject");
    setError(null);
    try {
      await api.post(`/api/admin/agent/proposals/${proposal.id}/reject`);
      setStatus("rejected");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("shared.agent.rejectFailed"));
    } finally {
      setBusy(null);
    }
  };

  // Outgoing-email steps: load the real subject/body so the operator reads + may rewrite before sending.
  const startEdit = async () => {
    setBusy("edit");
    setError(null);
    try {
      const res = await api.get<ApiResponse<AgentProposal>>(
        `/api/admin/agent/proposals/${proposal.id}`,
      );
      const ea = (res.data.executeArgs ?? {}) as Record<string, unknown>;
      setEditSubject(String(ea.subject ?? ""));
      setEditBody(String(ea.body ?? ""));
      setEditing(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("shared.agent.loadDraftFailed"));
    } finally {
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    setBusy("edit");
    setError(null);
    try {
      const res = await api.patch<ApiResponse<AgentProposal>>(
        `/api/admin/agent/proposals/${proposal.id}`,
        { subject: editSubject, body: editBody },
      );
      setSummaryOverride(res.data.summary ?? null);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("shared.agent.saveEditFailed"));
    } finally {
      setBusy(null);
    }
  };

  // Undo an executed content edit (universal content editor). Restores the before-snapshots; any
  // field changed since is skipped and surfaced as a conflict note.
  const revert = async () => {
    setBusy("revert");
    setError(null);
    try {
      const res = await api.post<
        ApiResponse<{ reverted: number; conflicts: { label: string; reason: string }[] }>
      >(`/api/admin/agent/proposals/${proposal.id}/revert`);
      const conflicts = res.data.conflicts ?? [];
      if (conflicts.length === 0) {
        // Clean undo — mark done and drop the button.
        setReverted(true);
      } else {
        // Some fields changed since; those were left as-is. Keep the button so a retry is possible.
        setRevertNote(
          t("shared.agent.revertNote", {
            restored: res.data.reverted,
            skipped: conflicts.length,
            fields: conflicts.map((cf) => cf.label).join(", "),
          }),
        );
      }
    } catch (err) {
      // An "already reverted" race isn't a failure — reflect the done state.
      if (err instanceof ApiError && err.code === "invalid_state") {
        setReverted(true);
      } else {
        setError(err instanceof Error ? err.message : t("shared.agent.revertFailed"));
      }
    } finally {
      setBusy(null);
    }
  };

  const tierBadge = isMoney
    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
    : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800/60">
      <div className="flex items-start gap-2">
        <ClipboardCheck className="text-primary-600 dark:text-primary-400 mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
              {stepNumber ? <span className="text-gray-400">{stepNumber}. </span> : null}
              {proposal.title}
            </p>
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tierBadge}`}>
              {TIER_LABELS[proposal.tier]}
            </span>
          </div>
          {shownSummary && !editing && (
            <p className="mt-0.5 whitespace-pre-wrap text-xs text-gray-500 dark:text-gray-400">
              {shownSummary}
            </p>
          )}
        </div>
      </div>

      {/* Edit form (outgoing email) — read + rewrite before sending */}
      {editing && (
        <div className="mt-2 space-y-2">
          <input
            value={editSubject}
            onChange={(e) => setEditSubject(e.target.value)}
            placeholder={t("shared.agent.subjectPlaceholder")}
            className="focus:border-primary-500 w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            rows={6}
            placeholder={t("shared.agent.bodyPlaceholder")}
            className="focus:border-primary-500 w-full resize-y rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={saveEdit}
              disabled={busy !== null}
              className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy === "edit" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {t("shared.agent.saveText")}
            </button>
            <button
              onClick={() => setEditing(false)}
              disabled={busy !== null}
              className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              {t("common.actions.cancel")}
            </button>
          </div>
        </div>
      )}

      {shownError && <p className="mt-2 text-xs text-red-600">{shownError}</p>}

      {/* Outcome states */}
      {effectiveStatus === "executed" && (
        <div className="mt-2 space-y-1.5">
          <p className="flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t("shared.agent.approvedExecuted")}
          </p>
          {proposal.tool === "propose_content_edit" &&
            (reverted ? (
              <p className="flex items-center gap-1.5 text-xs text-gray-500">
                <Undo2 className="h-3.5 w-3.5" /> {t("shared.agent.reverted")}
              </p>
            ) : (
              <button
                onClick={revert}
                disabled={busy !== null}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {busy === "revert" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Undo2 className="h-3.5 w-3.5" />
                )}
                {t("shared.agent.revert")}
              </button>
            ))}
          {revertNote && <p className="text-xs text-amber-600">{revertNote}</p>}
        </div>
      )}
      {effectiveStatus === "rejected" && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
          <XCircle className="h-3.5 w-3.5" /> {t("shared.agent.dismissed")}
        </p>
      )}
      {effectiveStatus === "failed" && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
          <ShieldAlert className="h-3.5 w-3.5" /> {t("shared.agent.executionFailed")}
        </p>
      )}

      {/* Action gate */}
      {effectiveStatus === "proposed" &&
        !editing &&
        (allowed ? (
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {confirming && isMoney ? (
              <>
                <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                  {t("shared.agent.confirmTier", { tier: TIER_LABELS[proposal.tier] })}
                </span>
                <button
                  onClick={() => approve(true)}
                  disabled={busy !== null}
                  className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {busy === "approve" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  )}
                  {t("shared.agent.yesConfirm")}
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  disabled={busy !== null}
                  className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  {t("common.actions.cancel")}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => approve(false)}
                  disabled={busy !== null}
                  className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                >
                  {busy === "approve" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  )}
                  {t("shared.agent.approve")}
                </button>
                {editable && (
                  <button
                    onClick={startEdit}
                    disabled={busy !== null}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    {busy === "edit" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Pencil className="h-3.5 w-3.5" />
                    )}
                    {t("common.actions.edit")}
                  </button>
                )}
                <button
                  onClick={reject}
                  disabled={busy !== null}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {busy === "reject" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5" />
                  )}
                  {t("shared.agent.dismiss")}
                </button>
              </>
            )}
          </div>
        ) : (
          <p className="mt-2 text-xs italic text-gray-400">{t("shared.agent.awaitingOperator")}</p>
        ))}
    </div>
  );
}
